import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  buildFreeSfxQuery,
  buildPremiumSfxQuery,
  deduplicateSearchHits,
} from '@music-vine/setlist';
import { searchKeys } from '@/utils/queryKeys';
import { setlist } from '@/utils/typesense';

import type {
  SearchResponse,
  SearchResponseHit,
  SfxDocument,
  SortOption,
} from '@music-vine/setlist';
import type { SortScoreVersion } from '@/atoms/typesenseDebug';

interface UseFreeFirstSfxSearchArgs {
  contextPath: string;
  // `false` on global /sfx/all; the page's public path on collection/category
  // routes, so split sections hit the same engagement-sorted collection.
  contextSlug: string | false;
  filters: string;
  query: string;
  sort: SortOption;
  sortScoreVersion: SortScoreVersion;
}

export interface FreeFirstSfxTierResult {
  hasMore: boolean;
  hits: SearchResponseHit<SfxDocument>[];
  isFetchingMore: boolean;
  onShowMore: () => void;
  // Pages loaded for this tier — the `pageIndex` analytics dimension.
  pageCount: number;
}

const FREE_TIER_PER_PAGE = 25;
// Premium teaser: 5 first, then 25 per "Show more" (offset-based to stay contiguous).
const PREMIUM_TEASER_SIZE = 5;
const PREMIUM_PAGE_SIZE = 25;

// 5 minutes — matches the single-stream search query's cache lifetime.
const STALE_TIME_MS = 300_000;

// Dedupe on `sfx_variant_id` (the playback identity), not `sfx_id`.
const sfxKey = (document: SfxDocument) => document.sfx_variant_id;

type TierPage = {
  expectedSize: number;
  hits: SearchResponseHit<SfxDocument>[];
  page: number;
};

// Premium paginates by offset with a small first page, so page 1 shows the
// 5-clip teaser and each later "Show more" appends 25 contiguous clips.
const premiumPageWindow = (page: number) =>
  page === 1
    ? { perPage: PREMIUM_TEASER_SIZE, offset: 0 }
    : {
        perPage: PREMIUM_PAGE_SIZE,
        offset: PREMIUM_TEASER_SIZE + (page - 2) * PREMIUM_PAGE_SIZE,
      };

// Each tier is its own `is_premium`-filtered search: the split lives in the
// query filter, not in slicing a combined response, so it stays correct on
// every sort. Not SSR-prefetched — fetches on mount when the layout is active.
export const useFreeFirstSfxSearch = ({
  query,
  filters,
  sort,
  sortScoreVersion,
  contextPath,
  contextSlug,
}: UseFreeFirstSfxSearchArgs) => {
  const buildTierSearch = (tier: 'free' | 'premium', page: number) => {
    if (tier === 'premium') {
      const { perPage, offset } = premiumPageWindow(page);
      // Drop the builder's `page` — Typesense ignores `offset` when `page` is set.
      const { page: _page, ...search } = buildPremiumSfxQuery({
        q: query,
        filter_by: filters,
        sort,
        page,
        sortScoreVersion,
        contextSlug,
        perPage: perPage * 2,
      });
      return {
        search: { ...search, offset, per_page: perPage },
        expectedSize: perPage,
      };
    }

    return {
      search: buildFreeSfxQuery({
        q: query,
        filter_by: filters,
        sort,
        page,
        sortScoreVersion,
        contextSlug,
        perPage: FREE_TIER_PER_PAGE * 2,
      }),
      expectedSize: FREE_TIER_PER_PAGE,
    };
  };

  const tierQueryOptions = (tier: 'free' | 'premium') => ({
    queryKey: searchKeys.sfxTier({
      tier,
      query,
      filters,
      sort,
      sortScoreVersion,
      contextPath,
      contextSlug,
    }),
    queryFn: async ({
      pageParam,
    }: {
      pageParam: number;
    }): Promise<TierPage> => {
      const { search, expectedSize } = buildTierSearch(tier, pageParam);
      const { results } = (await setlist.multiSearch(
        { searches: [search] },
        { use_cache: true }
      )) as { results: SearchResponse<SfxDocument>[] };

      return { hits: results[0]?.hits ?? [], page: pageParam, expectedSize };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: TierPage) =>
      lastPage.hits.length >= lastPage.expectedSize
        ? lastPage.page + 1
        : undefined,
    staleTime: STALE_TIME_MS,
  });

  const freeTier = useInfiniteQuery(tierQueryOptions('free'));
  const premiumTier = useInfiniteQuery(tierQueryOptions('premium'));

  const free = useMemo<FreeFirstSfxTierResult>(() => {
    const hits = deduplicateSearchHits(
      (freeTier.data?.pages ?? []).flatMap((p) => p.hits),
      sfxKey
    );

    return {
      hits,
      hasMore: !!freeTier.hasNextPage,
      isFetchingMore: freeTier.isFetchingNextPage,
      onShowMore: freeTier.fetchNextPage,
      pageCount: freeTier.data?.pages.length ?? 0,
    };
  }, [
    freeTier.data,
    freeTier.hasNextPage,
    freeTier.isFetchingNextPage,
    freeTier.fetchNextPage,
  ]);

  const premium = useMemo<FreeFirstSfxTierResult>(() => {
    const hits = deduplicateSearchHits(
      (premiumTier.data?.pages ?? []).flatMap((p) => p.hits),
      sfxKey
    );

    return {
      hits,
      hasMore: !!premiumTier.hasNextPage,
      isFetchingMore: premiumTier.isFetchingNextPage,
      onShowMore: premiumTier.fetchNextPage,
      pageCount: premiumTier.data?.pages.length ?? 0,
    };
  }, [
    premiumTier.data,
    premiumTier.hasNextPage,
    premiumTier.isFetchingNextPage,
    premiumTier.fetchNextPage,
  ]);

  return {
    free,
    premium,
    isLoading: freeTier.isLoading || premiumTier.isLoading,
    isError: freeTier.isError || premiumTier.isError,
  };
};
